import React from 'react';

const EDGE_PADDING = 12;
const ESC_KEY_CODE = 27;

export type Position = {
  x: number;
  y: number;
};

export type BoundingBox = Position & {
  width: number;
  height: number;
};

export interface ContextMenuOption {
  className?: string;
  children: any;
  onSelect?: () => void;
}

const getElementRefWidthAndHeight = (elementRef: React.RefObject<HTMLDivElement>) => {
  if (!elementRef.current) {
    return { height: -Infinity, width: -Infinity };
  }
  return {
    height: elementRef.current.clientHeight,
    width: elementRef.current.clientWidth,
  };
};

export const useContextMenuPosition = (
  menuContainerRef: React.RefObject<HTMLDivElement>,
  inputOptions: any[],
  position: { x: number; y: number },
  boundingBox: { x: number; y: number; width: number; height: number },
  timestamp: number,
  rootElement?: HTMLDivElement | null | undefined,
) => {
  const [options, setOptions] = React.useState(inputOptions);
  const [xPos, setXPos] = React.useState(-Infinity);
  const [yPos, setYPos] = React.useState(-Infinity);
  const { x, y } = position;

  React.useEffect(() => {
    setOptions(inputOptions);
  }, [inputOptions, timestamp]);

  React.useEffect(() => {
    const hideContextMenu = () => {
      setOptions([]);
    };

    const handleKeydown = (event: any) => {
      if (event.keyCode === ESC_KEY_CODE) {
        hideContextMenu();
      }
    };

    const clickListenerEl = rootElement || document;

    clickListenerEl.addEventListener('click', hideContextMenu);
    clickListenerEl.addEventListener('contextmenu', hideContextMenu);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      clickListenerEl.removeEventListener('click', hideContextMenu);
      clickListenerEl.removeEventListener('contextmenu', hideContextMenu);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [rootElement, setOptions]);

  React.useEffect(() => {
    const getTargetXY = () => {
      const { x: bbX, y: bbY, width: bbWidth, height: bbHeight } = boundingBox;
      const { width: menuWidth, height: menuHeight } =
        getElementRefWidthAndHeight(menuContainerRef);
      const maxX = bbWidth - menuWidth - EDGE_PADDING;
      const maxY = bbHeight - menuHeight - EDGE_PADDING;

      return { x: Math.min(x - bbX, maxX), y: Math.min(y - bbY, maxY) };
    };

    const asyncSetPosition = setTimeout(() => {
      // When the ContextMenu will be offscreen, it needs to
      // recalculate its position after determining its bounding box.
      const { x, y } = getTargetXY();
      setXPos(x);
      setYPos(y);
    });

    return () => {
      clearTimeout(asyncSetPosition);
    };
  }, [x, y, timestamp, boundingBox, menuContainerRef]);

  return { xPos, yPos, options };
};

export const ContextMenuItem: React.FC<ContextMenuOption> = ({
  className,
  children,
  onSelect,
}: ContextMenuOption) => {
  className = className || '';
  return (
    <button
      className={`whitespace-nowrap leading-relaxed ${className}`}
      disabled={!onSelect}
      onClick={onSelect}
    >
      {children}
    </button>
  );
};
